import type React from 'react';
import {
  type FieldExtensionComponentProps,
  type FieldSchema,
  makeFieldSchema,
} from '@backstage/plugin-scaffolder-react';
import {
  createFormField,
  type FormField,
} from '@backstage/plugin-scaffolder-react/alpha';
import { Alert, Link } from '@backstage/ui';

const SAFE_LINK_URL_PATTERN = /^[Hh][Tt][Tt][Pp][Ss]?:\/\//;

export type AlertFieldUiOptions = {
  icon?: boolean;
  link?: {
    text: string;
    url: string;
  };
  status?: 'info' | 'success' | 'warning' | 'danger';
};

export const alertFieldSchema: FieldSchema<null, AlertFieldUiOptions> =
  makeFieldSchema({
    output: z => z.null(),
    uiOptions: z =>
      z.object({
        icon: z.boolean().optional(),
        link: z
          .object({
            text: z.string().min(1),
            url: z.string().url().regex(SAFE_LINK_URL_PATTERN),
          })
          .optional(),
        status: z.enum(['info', 'success', 'warning', 'danger']).optional(),
      }),
  });

export function AlertFieldComponent(
  props: FieldExtensionComponentProps<null, AlertFieldUiOptions>,
): React.ReactElement {
  const options = props.uiSchema['ui:options'];
  const status = options?.status ?? 'info';
  const configuredLink = options?.link;
  const link =
    configuredLink && SAFE_LINK_URL_PATTERN.test(configuredLink.url)
      ? configuredLink
      : undefined;

  return (
    <Alert
      description={
        <>
          {props.schema.description}
          {link ? (
            <>
              {' '}
              <Link href={link.url} rel="noopener noreferrer" target="_blank">
                {link.text}
              </Link>
            </>
          ) : null}
        </>
      }
      icon={options?.icon}
      role={status === 'warning' || status === 'danger' ? 'alert' : 'status'}
      status={status}
      title={props.schema.title}
    />
  );
}

export const AlertField: FormField = createFormField({
  component: AlertFieldComponent,
  name: 'AlertField',
  schema: alertFieldSchema,
});
